import { clamp, uniq } from "lodash";
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { BuildView } from "~/components/build/Show/lib/types/BuildView";
import { StateOrder } from "~/components/build/Show/components/Sidebar/useFilterStore";

export enum DockPosition {
  Right = "right",
  Bottom = "bottom",
  Center = "center",
  Full = "full",
}

export enum StepsDisplayMode {
  Canvas = "canvas",
  Table = "table",
  Waterfall = "waterfall",
}

const SIDEBAR_DEFAULT_WIDTH = 320;
const SIDEBAR_MAX_WIDTH = 440;
const SIDEBAR_MIN_WIDTH = 230;
const DRAWER_DEFAULT_SIZE = 50;
const DRAWER_DEFAULT_MODE = DockPosition.Center;

export function clampSidebarWidth(value: number) {
  return clamp(value, SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH);
}

interface BuildPreferences {
  stepsDisplayMode: StepsDisplayMode;
  setStepsDisplayMode: (view: StepsDisplayMode) => void;

  /**
   * Default view for the build page, which can be overridden by the URL.
   * This is used to set the initial view when the page loads.
   *
   * If a string is provided, it will be used as the default view for all pipelines.
   * If an object is provided, it will map pipeline names to their respective default views.
   */
  defaultView: Record<string, BuildView> | BuildView;
  getDefaultView: (pipeline: string) => BuildView;
  setDefaultView: (view: BuildView, pipeline?: string) => void;

  dockPosition: DockPosition;
  setDockPosition: (dockPosition: DockPosition) => void;

  /**
   * Resized sidebar width (as pixels)
   */
  sidebarWidth?: number;
  setSidebarWidth: (width: number) => void;
  isSidebarOpen?: boolean;
  toggleSidebar: () => void;

  /**
   * Resized drawer height (in percentage)
   */
  resizedDrawerHeight?: number;
  setResizedDrawerHeight: (height: number) => void;
  resetDrawerHeight: () => void;

  /**
   * Resized drawer width (in percentage)
   */
  resizedDrawerWidth?: number;
  setResizedDrawerWidth: (width: number) => void;
  resetDrawerWidth: () => void;

  /**
   * Table view sort column and direction
   */
  sortColumn?: string | null;
  setSortColumn: (column: string | null) => void;
  sortDirection?: "asc" | "desc" | null;
  setSortDirection: (direction: "asc" | "desc" | null) => void;

  /**
   * Expanded/collapsed states (ie. failed, passed, etc.) in the sidebar
   */
  expandedStates?: string[];
  setExpandedStates: (states: string[]) => void;
  expandState: (state: string) => void;

  /**
   * Column widths
   */
  columnWidths: Record<string, number>;
  setColumnWidth: (key: string, width: number) => void;
  resetColumnWidths: () => void;

  /**
   * Show retried jobs (previous job attempts that were retried) in the build table
   */
  showRetriedJobs: boolean;
  setShowRetriedJobs: (show: boolean) => void;
}

/**
 * A store for build page preferences persisted into local storage to ensure
 * the user's preferences are remembered between sessions.
 */
export const useBuildPreferencesStore = create<BuildPreferences>()(
  persist(
    (set, get) => ({
      stepsDisplayMode: StepsDisplayMode.Canvas,
      dockPosition: DRAWER_DEFAULT_MODE,
      resizedDrawerHeight: DRAWER_DEFAULT_SIZE,
      resizedDrawerWidth: DRAWER_DEFAULT_SIZE,
      sidebarWidth: SIDEBAR_DEFAULT_WIDTH,
      isSidebarOpen: true,

      toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      setStepsDisplayMode: (mode: StepsDisplayMode) => set(() => ({ stepsDisplayMode: mode })),

      expandedStates: [...StateOrder],
      setExpandedStates: (expandedStates: string[]) => set(() => ({ expandedStates })),
      expandState: (expandedState: string) =>
        set((state) => ({
          expandedStates: uniq([...(state.expandedStates ?? []), expandedState]),
        })),

      setSortColumn: (column: string | null) => set(() => ({ sortColumn: column })),
      setSortDirection: (direction: "asc" | "desc" | null) =>
        set(() => ({ sortDirection: direction })),

      setSidebarWidth: (width: number) => {
        return set(() => ({ sidebarWidth: clampSidebarWidth(width) }));
      },

      setDockPosition: (dock: DockPosition) => set(() => ({ dockPosition: dock })),

      resetDrawerHeight: () => set(() => ({ resizedDrawerHeight: DRAWER_DEFAULT_SIZE })),
      setResizedDrawerHeight: (height: number) => set(() => ({ resizedDrawerHeight: height })),

      resetDrawerWidth: () => set(() => ({ resizedDrawerWidth: DRAWER_DEFAULT_SIZE })),
      setResizedDrawerWidth: (width: number) => set(() => ({ resizedDrawerWidth: width })),

      columnWidths: {},
      setColumnWidth: (key, width) =>
        set((state) => ({
          columnWidths: { ...state.columnWidths, [key]: width },
        })),
      resetColumnWidths: () => set({ columnWidths: {} }),

      showRetriedJobs: false,
      setShowRetriedJobs: (show: boolean) => set(() => ({ showRetriedJobs: show })),

      defaultView: BuildView.Canvas,

      /**
       * Get the default view for navigation, based on stored preferences and feature flag fallbacks
       */
      getDefaultView: (pipeline: string) => {
        let view: BuildView;

        if (typeof get().defaultView === "string") {
          view = get().defaultView as BuildView;
        } else {
          view = get().defaultView[pipeline] || get().defaultView["*"] || BuildView.Canvas;
        }

        if (view === BuildView.Waterfall && Features.DisableStepsWaterfallView) {
          // Fall back to table view if the user has set Waterfall as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          return BuildView.Table;
        }

        if (view === BuildView.Canvas && Features.DisableStepsCanvasView) {
          // Fall back to table view if the user has set Canvas as their preference
          // We won't persist this to their preferences as they may belong to other
          // organizations where this would be surprising
          return BuildView.Table;
        }

        return view;
      },
      setDefaultView: (view: BuildView, pipeline?: string) => {
        if (pipeline) {
          set((state) => {
            if (typeof state.defaultView === "string") {
              return {
                defaultView: {
                  [pipeline]: view,
                  // Preserve the default view set for all other pipelines
                  "*": state.defaultView,
                },
              };
            }

            return {
              defaultView: {
                ...state.defaultView,
                [pipeline]: view,
              },
            };
          });
        } else {
          set(() => ({
            defaultView: view,
          }));
        }
      },
    }),
    {
      name: "build-preferences",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);
